import React, { useEffect, useState } from "react";
import { IoDownloadOutline, IoEyeOutline, IoAddOutline } from "react-icons/io5";
import { useNavigate, useParams } from "react-router-dom";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import { motion } from "framer-motion";
import axios from "axios";
import { useUser } from "../../context/UserContext";
import api from "../../utils/api";

const Payslips = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [payslips, setPayslips] = useState([]);
  const { empid } = useParams();
  const payslipsPerPage = 8;
  const navigate = useNavigate();

  // Add user context
  const { currentUser } = useUser();
  const baseRoute = "/hr";

  const handleClick = (payid) => {
    navigate(`${baseRoute}/employees/View/${payid}`);
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get(`/userpayslip/${empid}`);
        console.log("Fetched payslips:", response.data);
        const data = response.data;
        setPayslips(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching employees:", error);
        setPayslips([]);
      }
    };

    fetchEmployees();
  }, []);

  const handleDownload = async (payid, salaryPeriod, employeeId) => {
    try {
      const response = await api.get(`/payslip/download/${payid}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${employeeId}_payslip_${salaryPeriod}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Failed to download PDF:", error);
    }
  };

  const totalPages = Math.ceil(payslips.length / payslipsPerPage);
  const startIndex = (currentPage - 1) * payslipsPerPage;
  const currentPayslips = payslips.slice(startIndex, startIndex + payslipsPerPage);

  return (
    <div className="p-4 sm:p-6 w-full mx-auto">
      {/* Header Section */}
      <div className="w-full mb-6 flex items-center justify-between">
        <div>
          
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Employee Payslips</h1>
        </div>
        <button onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 transition-colors px-4 py-2 rounded-xl hover:bg-white border border-transparent hover:border-slate-200">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back
        </button>
      </div>

      {/* Desktop Table View */}
      <motion.div
        className="hidden sm:block bg-white rounded-2xl shadow-md overflow-hidden"
        transition={{ duration: 0.5 }}
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-4 text-sm font-semibold text-slate-600 text-left">
                  S.No
                </th>
                <th className="px-3 py-4 text-sm font-semibold">Salary period</th>
                <th className="px-3 py-4 text-sm font-semibold">Amount</th>
                <th className="px-3 py-4 text-sm font-semibold">Status</th>
                <th className="px-3 py-4 text-sm font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {currentPayslips.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-16 text-center">
                    <div className="flex flex-col items-center gap-2 text-slate-400">
                      <span className="text-5xl">🧾</span>
                      <span className="text-sm font-medium">No payslips found</span>
                      <span className="text-xs text-slate-300">
                        There are no payslips available for this employee.
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                currentPayslips.map((payslip, index) => (
                  <tr
                    key={payslip.payid}
                    className="hover:bg-slate-50/50 transition-colors duration-150"
                  >
                    <td className="px-3 py-2 text-sm text-slate-600">
                      {(currentPage - 1) * payslipsPerPage + index + 1}
                    </td>
                    <td className="px-3 py-2 text-sm">{payslip.salaryPeriod}</td>
                    <td className="px-3 py-2 text-sm font-medium">
                      {payslip.netPayable}
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                        Approved
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <div className="flex items-center gap-4">
                        <motion.button
                          onClick={() => handleClick(payslip.payid)}
                          className="text-slate-600 hover:text-blue-600 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="View Payslip"
                        >
                          <IoEyeOutline className="text-xl" />
                        </motion.button>
                        <motion.button
                          onClick={() =>
                            handleDownload(
                              payslip.payid,
                              payslip.salaryPeriod,
                              payslip.employeeId
                            )
                          }
                          className="text-slate-600 hover:text-blue-600 transition-colors"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          title="Download Payslip"
                        >
                          <IoDownloadOutline className="text-xl" />
                        </motion.button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination — only show when there are payslips */}
        {payslips.length > 0 && (
          <div className="px-6 py-4 border-t border-slate-100">
            <Stack spacing={2} className="flex justify-end">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded-full"
                size="small"
                showFirstButton
                showLastButton
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#475569",
                    borderColor: "#e2e8f0",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#475569 !important",
                    color: "#ffffff",
                  },
                }}
              />
            </Stack>
          </div>
        )}
      </motion.div>

      {/* Mobile Card View */}
      <motion.div
        className="sm:hidden space-y-4"
        transition={{ duration: 0.5 }}
      >
        {currentPayslips.length === 0 ? (
          <div className="bg-white p-10 rounded-xl shadow-sm border border-slate-100 text-center">
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <span className="text-5xl">🧾</span>
              <span className="text-sm font-medium">No payslips found</span>
              <span className="text-xs text-slate-300">
                There are no payslips available for this employee.
              </span>
            </div>
          </div>
        ) : (
          currentPayslips.map((payslip) => (
            <motion.div
              key={payslip.payid}
              className="bg-white p-4 rounded-xl shadow-sm border border-slate-100"
              whileHover={{ scale: 1.01 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-slate-800">
                    {payslip.salaryPeriod}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-700">
                    Approved
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-xl font-semibold text-slate-700">
                    {payslip.netPayable}
                  </span>
                  <div className="flex gap-3">
                    <motion.button
                      onClick={() => handleClick(payslip.payid)}
                      className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="View Payslip"
                    >
                      <IoEyeOutline className="text-xl font-bold" />
                    </motion.button>
                    <motion.button
                      onClick={() =>
                        handleDownload(
                          payslip.payid,
                          payslip.salaryPeriod,
                          payslip.employeeId
                        )
                      }
                      className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      title="Download Payslip"
                    >
                      <IoDownloadOutline className="text-xl" />
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}

        {/* Mobile Pagination — only show when there are payslips */}
        {payslips.length > 0 && (
          <div className="mt-6 flex justify-center">
            <Stack spacing={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={(_, page) => setCurrentPage(page)}
                variant="outlined"
                shape="rounded"
                size="medium"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "#475569",
                    borderColor: "#e2e8f0",
                  },
                  "& .Mui-selected": {
                    backgroundColor: "#475569 !important",
                    color: "#ffffff",
                  },
                }}
              />
            </Stack>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Payslips;